import { memo, type ReactNode } from 'react';
import { Group, Slider, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import styles from './ZoomRangeSettings.module.css';

interface ZoomSliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
}

function ZoomSliderRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
  onChangeEnd,
}: ZoomSliderRowProps) {
  return (
    <Stack gap="xs">
      <Group justify="space-between" gap="xs">
        <Text size="sm">{label}</Text>
        <Text size="sm" className={styles.value}>
          {value}
        </Text>
      </Group>
      <Slider
        value={value}
        onChange={onChange}
        onChangeEnd={onChangeEnd}
        min={min}
        max={max}
        step={step}
        label={null}
        size="sm"
      />
    </Stack>
  );
}

export interface ZoomRangeSettingsProps {
  startZoom: number;
  minZoom: number;
  maxZoom: number;
  onStartZoomChange: (value: number) => void;
  onMinZoomChange: (value: number) => void;
  onMaxZoomChange: (value: number) => void;
  /** Commit callback fired when the start-zoom slider is released. */
  onStartZoomChangeEnd?: (value: number) => void;
  /** Slider bounds + step (e.g. global map config or map-setup constants). */
  min: number;
  max: number;
  step: number;
  /** Optional heading rendered above the sliders. */
  header?: ReactNode;
}

/**
 * Three coupled zoom sliders (start / min / max) shared by the workspace map
 * configurator and the share-link map settings.
 *
 * Clamping (start within [min, max]; raising min lifts dependents; lowering
 * max drops dependents) is owned by the parent form hook — this component only
 * renders the controls and forwards raw values.
 */
export const ZoomRangeSettings = memo(function ZoomRangeSettings({
  startZoom,
  minZoom,
  maxZoom,
  onStartZoomChange,
  onMinZoomChange,
  onMaxZoomChange,
  onStartZoomChangeEnd,
  min,
  max,
  step,
  header,
}: ZoomRangeSettingsProps) {
  const { t } = useTranslation();

  return (
    <Stack gap="md">
      {header}
      <ZoomSliderRow
        label={t('mapSetup.zoom.start')}
        value={startZoom}
        min={min}
        max={max}
        step={step}
        onChange={onStartZoomChange}
        onChangeEnd={onStartZoomChangeEnd}
      />
      <ZoomSliderRow
        label={t('mapSetup.zoom.min')}
        value={minZoom}
        min={min}
        max={max}
        step={step}
        onChange={onMinZoomChange}
      />
      <ZoomSliderRow
        label={t('mapSetup.zoom.max')}
        value={maxZoom}
        min={min}
        max={max}
        step={step}
        onChange={onMaxZoomChange}
      />
    </Stack>
  );
});
